import type { ReactNode } from 'react'

/**
 * The pictures the frame uses.
 *
 * Drawn rather than downloaded: a font of icons is another network request that can fail, and a
 * picture that has not arrived leaves a control with no visible meaning. They are all one stroke
 * weight on one grid, take their colour from the text around them, and are hidden from a screen
 * reader — the name of a control lives on the control, never in the picture.
 */
export type IconName =
  | 'desk'
  | 'library'
  | 'search'
  | 'timetable'
  | 'diary'
  | 'cards'
  | 'back'
  | 'person'
  | 'warning'
  | 'check'
  | 'close'
  | 'notes'
  | 'tasks'
  | 'details'
  | 'up'
  | 'down'
  | 'grip'
  | 'strike'
  | 'expand'
  | 'collapse'
  | 'print'
  | 'sticky'
  | 'frame'
  | 'connect'
  | 'fit'
  | 'pin'
  | 'toFront'
  | 'toBack'
  | 'undo'
  | 'highlight'
  | 'zoomIn'
  | 'zoomOut'
  | 'blank'
  | 'rote'
  | 'subject'
  | 'plus'

const DRAWINGS: Readonly<Record<IconName, ReactNode>> = {
  /* One more of something. The one picture a control that makes things is allowed to carry. */
  plus: <path d="M12 5.5v13M5.5 12h13" />,
  /*
    A subject: a bracket around three things that belong together. Not a folder and not a tag —
    the picture has to say "these are one thing" rather than "these are filed here".
  */
  subject: (
    <>
      <path d="M5.5 4.5v15" />
      <path d="M5.5 8h4M5.5 12h7M5.5 16h4" />
      <path d="M17 6.5c1.5 0 2 1 2 2.5v6c0 1.5-.5 2.5-2 2.5" />
    </>
  ),
  desk: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <rect x="6" y="7.5" width="5.5" height="4.5" rx="1" />
      <rect x="14" y="7.5" width="4" height="9" rx="1" />
    </>
  ),
  library: (
    <>
      <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H19v14H6.5A2.5 2.5 0 0 0 4 19.5z" />
      <path d="M4 19.5A2.5 2.5 0 0 0 6.5 22H19v-5" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m15.8 15.8 4.7 4.7" />
    </>
  ),
  timetable: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </>
  ),
  diary: (
    <>
      <path d="M7.5 3H19v18H7.5z" />
      <path d="M7.5 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h1.5" />
      <path d="M11 3v6l2-1.5L15 9V3" />
    </>
  ),
  cards: (
    <>
      <rect x="7" y="3" width="13" height="13" rx="2" />
      <path d="M16 20.5H6.5A2.5 2.5 0 0 1 4 18V8" />
    </>
  ),
  back: <path d="m14.5 5-7 7 7 7" />,
  person: (
    <>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M4.5 20.5a7.5 7.5 0 0 1 15 0" />
    </>
  ),
  warning: (
    <>
      <path d="M12 3.5 2.8 20h18.4z" />
      <path d="M12 10v4.5M12 17.5h.01" />
    </>
  ),
  check: <path d="m4.5 12.5 5 5 10-11" />,
  close: <path d="m6 6 12 12M18 6 6 18" />,
  /** Nothing has happened to this yet. */
  blank: <circle cx="12" cy="12" r="7.5" />,
  /** Round and round: what has to be produced again rather than asked. */
  rote: (
    <>
      <path d="M5 12a7 7 0 0 1 11.9-5M19 12a7 7 0 0 1-11.9 5" />
      <path d="M17.5 3.5V7H14M6.5 20.5V17H10" />
    </>
  ),
  notes: (
    <>
      <path d="M5 4.5h11l3 3V20a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 20z" />
      <path d="M16 4.5V8h3" />
      <path d="M8.5 12h7M8.5 15.5h4.5" />
    </>
  ),
  /* Open points: two of them done, one still waiting. A list with ticks, not a clock — nothing here
     rings, and a bell would promise something this area deliberately does not do. */
  tasks: (
    <>
      <path d="m3.5 7 1.8 1.8L8.5 5.5" />
      <path d="m3.5 16 1.8 1.8 3.2-3.3" />
      <path d="M12 7.5h8.5M12 16.5h8.5" />
    </>
  ),
  /* "There is more to this one." A chevron rather than a pencil: it opens the task, it does not
     promise a form. */
  details: <path d="m9.5 5 7 7-7 7" />,
  grip: (
    <>
      <path d="M9 6h.01M9 12h.01M9 18h.01M15 6h.01M15 12h.01M15 18h.01" />
    </>
  ),
  expand: (
    <>
      <path d="M9 4.5H4.5V9M15 4.5h4.5V9M9 19.5H4.5V15M15 19.5h4.5V15" />
    </>
  ),
  collapse: (
    <>
      <path d="M4.5 9H9V4.5M19.5 9H15V4.5M4.5 15H9v4.5M19.5 15H15v4.5" />
    </>
  ),
  /* A printer: the sheet going in above, the sheet coming out below. The one picture that is the
     same picture everywhere, which is why it may be shown without its name written beside it. */
  print: (
    <>
      <path d="M7.5 9.5V4.5h9v5" />
      <path d="M7.5 17.5H6A2 2 0 0 1 4 15.5v-4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-1.5" />
      <path d="M7.5 14h9v5.5h-9z" />
    </>
  ),
  strike: (
    <>
      <path d="M4 12h16" />
      <path d="M7 7.5c0-1.7 2.2-3 5-3s5 1.3 5 3" />
      <path d="M17 16.5c0 1.7-2.2 3-5 3s-5-1.3-5-3" />
    </>
  ),
  up: <path d="m5.5 14.5 6.5-6.5 6.5 6.5" />,
  down: <path d="m5.5 9.5 6.5 6.5 6.5-6.5" />,
  /* A square with its corner turned up: the thing everybody draws when they mean a sticky note. */
  sticky: (
    <>
      <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5V14l-6 6H5.5A1.5 1.5 0 0 1 4 18.5z" />
      <path d="M20 14h-4.5A1.5 1.5 0 0 0 14 15.5V20" />
    </>
  ),
  /* A rectangle drawn around things — dashed, because it encloses rather than contains. */
  frame: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" strokeDasharray="3 2.5" />
      <rect x="7" y="9" width="4" height="4" rx="1" />
      <rect x="13" y="9" width="4" height="6" rx="1" />
    </>
  ),
  /* Two things and the line between them. */
  connect: (
    <>
      <circle cx="6" cy="6" r="2.5" />
      <circle cx="18" cy="18" r="2.5" />
      <path d="M8 8.2 15.8 16" />
    </>
  ),
  /* Four corners pointing inwards at a rectangle: everything brought into view. */
  fit: (
    <>
      <path d="M3 8V4.5A1.5 1.5 0 0 1 4.5 3H8M16 3h3.5A1.5 1.5 0 0 1 21 4.5V8M21 16v3.5a1.5 1.5 0 0 1-1.5 1.5H16M8 21H4.5A1.5 1.5 0 0 1 3 19.5V16" />
      <rect x="8" y="8" width="8" height="8" rx="1" />
    </>
  ),
  /* A drawing pin from the side: this stays where it is put. */
  pin: (
    <>
      <path d="M9 3h6l-1 6 3.5 3.5H6.5L10 9z" />
      <path d="M12 12.5V21" />
    </>
  ),
  toFront: (
    <>
      <rect x="8" y="8" width="12" height="12" rx="2" />
      <path d="M16 4H5.5A1.5 1.5 0 0 0 4 5.5V16" />
    </>
  ),
  toBack: (
    <>
      <rect x="4" y="4" width="12" height="12" rx="2" />
      <path d="M8 20h10.5A1.5 1.5 0 0 0 20 18.5V8" />
    </>
  ),
  /* An arrow curving back on itself. */
  undo: (
    <>
      <path d="M4 9h10a5.5 5.5 0 0 1 0 11H8" />
      <path d="m8 4.5-4 4.5 4 4.5" />
    </>
  ),
  /* A pen nib over a line: marked out, without depending on telling colours apart. */
  highlight: (
    <>
      <path d="M4 20h16" />
      <path d="m6.5 16.5 8.5-8.5 3 3-8.5 8.5H6.5z" />
      <path d="m15 8 2-2a1.6 1.6 0 0 1 2.3 0l.7.7a1.6 1.6 0 0 1 0 2.3l-2 2" />
    </>
  ),
  zoomIn: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m15.8 15.8 4.7 4.7M8.5 11h5M11 8.5v5" />
    </>
  ),
  zoomOut: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m15.8 15.8 4.7 4.7M8.5 11h5" />
    </>
  ),
}

export interface IconProps {
  readonly name: IconName
  /** Half size, for a picture that sits inside a line of text rather than above a label. */
  readonly small?: boolean
}

export function Icon({ name, small = false }: IconProps) {
  return (
    <svg
      className={small ? 'icon icon--small' : 'icon'}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {DRAWINGS[name]}
    </svg>
  )
}
